import _Vue, { VNode } from 'vue'
import { layout } from '@/design/tokens'

// The single choke point for every toast: eslint.config.mjs bans `$bvToast.toast` elsewhere, so the two
// shapes, the toaster position and the auto-dismiss split cannot drift across call sites. It does NOT cap the
// stack: the cap and the message coalescing it forces are behaviour, deferred in docs/decisions/0002-toast-policy.md.

/** `'5000ms'` -> `5000`: the generated token module types every value as a string. */
function toNumber(token: string): number {
  return parseInt(token, 10)
}

const AUTO_HIDE_DELAY = toNumber(layout.timingToastDismiss)

let counter = 0

/** Ids exist so the single-line shapes can hide the toast they belong to. */
function nextId(): string {
  counter += 1

  return `blytz-toast-${counter}`
}

interface ToastPayload {
  id: string
  toaster: string
  variant: string
  appendToast: boolean
  noAutoHide: boolean
  title?: string
  autoHideDelay?: number
  noCloseButton?: boolean
}

/**
 * Dismiss control for the single-line shapes, which suppress the native header.
 * A real <button>, not a span with role="button": the header button it stands in
 * for is focusable, and a span without a tab stop cannot be reached by keyboard.
 * It carries Bootstrap's `.close` class so both hosts share one set of styles.
 */
function dismissButton(vm: _Vue, id: string): VNode {
  const h = vm.$createElement

  return h(
    'button',
    {
      class: 'close blytz-toast__dismiss',
      attrs: { type: 'button', 'aria-label': vm.$t('buttons.dismiss') },
      on: { click: () => vm.$bvToast.hide(id) }
    },
    '×'
  )
}

/** An inline button that runs the reader's next step, such as Reload. */
function actionButton(vm: _Vue, action: ToastAction): VNode {
  return vm.$createElement(
    'button',
    {
      class: 'blytz-toast__action',
      attrs: { type: 'button', 'data-testid': 'toast-action' },
      on: { click: action.onClick }
    },
    action.label
  )
}

function fire(
  vm: _Vue,
  message: string | VNode[],
  payload: ToastPayload
): void {
  // BootstrapVue accepts a VNode array as the message; its types say string.
  vm.$bvToast.toast(message as string, payload)
}

function basePayload(id: string, variant: string): ToastPayload {
  return {
    id,
    variant,
    toaster: 'b-toaster-bottom-center',
    appendToast: true,
    noAutoHide: false
  }
}

/**
 * Whole-form, network and session failures, and every payment decline.
 * Manual-dismiss only. `title` renders in bold above the message.
 */
function error(vm: _Vue, message: string, title: string): void {
  fire(vm, message, {
    ...basePayload(nextId(), 'danger'),
    title,
    noAutoHide: true
  })
}

/**
 * `persist` holds the toast until the reader dismisses it. The shape already
 * carries its own dismiss button, so staying put never traps anyone. Used for a
 * message the reader has to act on rather than merely notice: a retry cap names
 * the time they may pay again, and five seconds is not long enough to read and
 * remember an instant.
 */
function singleLine(
  vm: _Vue,
  variant: string,
  message: string,
  persist = false,
  action?: ToastAction
): string {
  const id = nextId()
  const h = vm.$createElement

  fire(
    vm,
    [
      h('span', { class: 'blytz-toast__message' }, message),
      ...(action ? [actionButton(vm, action)] : []),
      dismissButton(vm, id)
    ],
    {
      ...basePayload(id, variant),
      ...(persist ? { noAutoHide: true } : { autoHideDelay: AUTO_HIDE_DELAY }),
      noCloseButton: true
    }
  )

  return id
}

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface BlytzToast {
  error(message: string, title: string): void
  /** @returns the toast id, for `hide`. */
  success(message: string): string
  /** @returns the toast id, for `hide`. */
  info(
    message: string,
    options?: { persist?: boolean; action?: ToastAction }
  ): string
  hide(id: string): void
}

export default {
  install(Vue: typeof _Vue) {
    // A getter, not an assignment: `$bvToast` resolves the toaster relative to
    // the instance it is called on, so each caller needs its own binding.
    Object.defineProperty(Vue.prototype, '$toast', {
      get(this: _Vue): BlytzToast {
        const vm = this

        return {
          error: (message, title) => error(vm, message, title),
          success: message => singleLine(vm, 'success', message),
          info: (message, options) =>
            singleLine(vm, 'info', message, options?.persist, options?.action),
          hide: id => vm.$bvToast.hide(id)
        }
      }
    })
  }
}

/** Test seam: the id counter is module state and would leak between specs. */
export function __resetToastIds(): void {
  counter = 0
}
